import { ref } from 'vue'
import { defineStore } from 'pinia'
import type { DataExportDto } from '@roadfootage/api-client'

import { useApi } from '../../composables/api'
import { readApiErrorMessage } from '../../utils/api-error'

export type DataExportActionResult = { ok: true } | { ok: false; message: string }

const REQUEST_ERROR = 'Could not request your data. Please try again.'
const DOWNLOAD_ERROR = 'Could not start the download. Please try again.'

/**
 * "Download my data": the newest export, when another can be requested, and the two actions. Silent api (no global toast), so
 * the card shows the server's own text, such as the 24 hour limit.
 */
export const useDataExportStore = defineStore('dataExport', () => {
  const { meDataExportsApi } = useApi({ notifyOnError: false })

  const latest = ref<DataExportDto | null>(null)
  const nextRequestAt = ref<Date | null>(null)
  const loaded = ref(false)
  const loadFailed = ref(false)
  const requesting = ref(false)
  const downloading = ref(false)

  async function load() {
    loadFailed.value = false
    try {
      const state = await meDataExportsApi.meDataExportsGetDataExportState()
      latest.value = state.latest ?? null
      nextRequestAt.value = state.nextRequestAt ?? null
    } catch (error) {
      console.error('Failed to load the data export state', error)
      loadFailed.value = true
    } finally {
      loaded.value = true
    }
  }

  async function request(): Promise<DataExportActionResult> {
    if (requesting.value) return { ok: false, message: 'Already requesting your data.' }
    requesting.value = true
    try {
      latest.value = await meDataExportsApi.meDataExportsRequestDataExport()
    } catch (error) {
      console.error('Failed to request a data export', error)
      const message = await readApiErrorMessage(error, REQUEST_ERROR)
      requesting.value = false
      // A refusal (limit, one already running) means the card is out of date.
      await load()
      return { ok: false, message }
    }
    requesting.value = false
    await load()
    return { ok: true }
  }

  async function download(id: string): Promise<DataExportActionResult> {
    if (downloading.value) return { ok: false, message: 'Already starting a download.' }
    downloading.value = true
    try {
      const link = await meDataExportsApi.meDataExportsCreateDataExportDownloadUrlById({ id })
      if (!link.url) throw new Error('The server returned no download link.')
      window.location.assign(link.url)
    } catch (error) {
      console.error('Failed to get the data export link', error)
      const message = await readApiErrorMessage(error, DOWNLOAD_ERROR)
      downloading.value = false
      await load()
      return { ok: false, message }
    }
    downloading.value = false
    return { ok: true }
  }

  return {
    latest,
    nextRequestAt,
    loaded,
    loadFailed,
    requesting,
    downloading,
    load,
    request,
    download
  }
})
